// src/components/admin/LeaveManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, CheckCircle, XCircle, Clock, AlertCircle, RotateCcw } from 'lucide-react';
import { api } from '../../services/api';
import leaveService from '../../services/leave.service';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useEmployees } from '../../contexts/EmployeeContext';

const LeaveManagement = () => {
    const { getCompletedOnboarding } = useOnboarding();
    const { employees } = useEmployees();

    const [leaveRequests, setLeaveRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('pending');

    const onboardedEmployees = getCompletedOnboarding();
    const onboardedCount = onboardedEmployees.length;
    const totalCount = employees.length;

    const fetchLeaveRequests = useCallback(async () => {
        try {
            const response = await api.get(`/admin/leaves?status=${filterStatus}`);
            setLeaveRequests(response.data.requests || []);
        } catch (error) {
            console.error('Error fetching leave requests:', error);
        } finally {
            setLoading(false);
        }
    }, [filterStatus]);

    // Fetch data when component mounts
    useEffect(() => {
        fetchLeaveRequests();
    }, [fetchLeaveRequests]);

    const handleApprove = async (requestId) => {
        try {
            await api.post(`/admin/leaves/${requestId}/approve`);
            fetchLeaveRequests();
        } catch (error) {
            console.error('Error approving leave:', error);
        }
    };

    const handleReject = async (requestId) => {
        const reason = prompt('Please provide a reason for rejection:');
        if (!reason) return;

        try {
            await api.post(`/admin/leaves/${requestId}/reject`, { reason });
            fetchLeaveRequests();
        } catch (error) {
            console.error('Error rejecting leave:', error);
        }
    };

    const handleRevoke = async (requestId) => {
        const reason = prompt('Please provide a reason for revoking this approved leave:');
        if (!reason || reason.trim() === '') {
            alert('Reason is required to revoke an approved leave.');
            return;
        }

        try {
            const result = await leaveService.revokeApprovedLeave(requestId, reason);
            alert(result.message || 'Leave revoked successfully');
            fetchLeaveRequests();
        } catch (error) {
            console.error('Error revoking leave:', error);
            alert(error.response?.data?.error || 'Failed to revoke leave');
        }
    };

    if (loading) {
        return <div className="flex justify-center items-center h-64">Loading...</div>;
    }

    return (
        <div className="space-y-6">
            <h1 className="text-2xl font-bold text-gray-900">Leave Management</h1>

            {/* Onboarding Status Info Banner */}
            {onboardedCount < totalCount && (
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg">
                    <div className="flex items-center">
                        <AlertCircle className="w-5 h-5 text-blue-600 mr-3" />
                        <div>
                            <p className="text-sm font-medium text-blue-900">
                                {onboardedCount} of {totalCount} employees have completed onboarding
                            </p>
                            <p className="text-xs text-blue-700 mt-1">
                                Only onboarded employees can apply for leaves.
                                {totalCount - onboardedCount} {totalCount - onboardedCount === 1 ? 'employee' : 'employees'} pending onboarding.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-lg shadow p-4">
                <div className="flex gap-2">
                    {['pending', 'approved', 'rejected', 'all'].map(status => (
                        <button
                            key={status}
                            onClick={() => setFilterStatus(status)}
                            className={`px-4 py-2 rounded-lg ${
                                filterStatus === status
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                        </button>
                    ))}
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full">
                    <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Leave Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                    {leaveRequests.map((request) => (
                        <tr key={request.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div>
                                    <p className="text-sm font-medium">{request.employee_name}</p>
                                    <p className="text-xs text-gray-500">{request.employee_code}</p>
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">{request.leave_type}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {new Date(request.from_date).toLocaleDateString()} - {new Date(request.to_date).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">{request.days_requested}</td>
                            <td className="px-6 py-4 text-sm">{request.reason}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      request.status === 'approved' ? 'bg-green-100 text-green-800' :
                          request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              request.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                  }`}>
                    {request.status}
                  </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {request.status === 'pending' && (
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleApprove(request.id)}
                                            className="text-green-600 hover:text-green-900"
                                            title="Approve"
                                        >
                                            <CheckCircle className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={() => handleReject(request.id)}
                                            className="text-red-600 hover:text-red-900"
                                            title="Reject"
                                        >
                                            <XCircle className="w-5 h-5" />
                                        </button>
                                    </div>
                                )}
                                {request.status === 'approved' && (
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleRevoke(request.id)}
                                            className="text-orange-600 hover:text-orange-900 flex items-center gap-1"
                                            title="Revoke approved leave"
                                        >
                                            <RotateCcw className="w-4 h-4" />
                                            <span className="text-xs">Revoke</span>
                                        </button>
                                    </div>
                                )}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default LeaveManagement;